<template>
  <div class="nav-bar" :class="{ 'nav-bar-centered': isCenteredPath }">
    <div class="left-section">
      <img
        v-if="showBack"
        class="back-icon"
        src="@/assets/back.png"
        alt="back"
        @click="handleBack"
      />
    </div>
    <span class="title">{{ title }}</span>
    <div class="right-section">
      <LanguageSelector v-if="showLanguage" />
      <button v-if="showSubmit" class="submit-btn" @click="handleSubmit">
        {{ submitText }}
      </button>
      <img
        v-if="showMore"
        src="@/assets/more.png"
        alt="more"
        class="more-icon"
        @click="showOperate = true"
      />
    </div>
  </div>

  <!-- 引入 ShareModal 组件 -->
  <OperateModal v-model="showOperate" :show-delete="showDelete" />
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import OperateModal from "@/components/modal/OperateModal.vue";
// @ts-ignore
import LanguageSelector from "@/components/common/LanguageSelector.vue";

const props = withDefaults(
  defineProps<{
    title: string; // 标题，
    showSubmit?: boolean; // 是否显示提交按钮，
    submitText?: string; // 提交按钮文字，
    showMore?: boolean; // 是否显示更多按钮
    showDelete?: boolean; // 是否显示删除按钮
    showBack?: boolean; // 是否显示返回按钮
    showLanguage?: boolean; // 是否显示语言选择器
    customBack?: () => void; // 自定义返回方法
  }>(),
  {
    showBack: true, // 默认显示返回按钮
    showSubmit: false,
    showMore: false,
    submitText: "submit",
    showDelete: false,
    showLanguage: false,
  }
);

const emit = defineEmits<{
  (e: "submit"): void;
  (e: "back"): void;
}>();

const router = useRouter();
const route = useRoute();

// 这些路由下导航栏使用 max-width 800px 居中
const CENTERED_PATH_PREFIXES = ["/post/", "/reply/", "/shop"];
const isCenteredPath = computed(() =>
  CENTERED_PATH_PREFIXES.some((p) => route.path.startsWith(p))
);

// OperateModal 相关状态
const showOperate = ref(false);

const handleBack = () => {
  if (props.customBack) {
    props.customBack();
    return;
  } else {
    // 检查是否有可返回的历史页面
    if (window.history?.state && window.history.state.back) {
      // 如果有可返回的历史页面，正常返回
      router.back();
    } else {
      // 如果没有可返回的历史页面，跳转到首页
      router.push("/");
    }
    emit("back");
  }
};

const handleSubmit = () => {
  emit("submit");
};
</script>

<style scoped lang="scss">
.nav-bar {
  display: flex;
  align-items: center;
  height: 56px;
  width: 100%;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  padding: 0 16px;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;
  gap: 6px;

  &.nav-bar-centered {
    max-width: 800px;
    left: 50%;
    transform: translateX(-50%);
  }
}

.left-section {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.back-icon {
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.title {
  font-size: 18px;
  font-weight: bold;
  flex: 1;
  text-align: center;
  pointer-events: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0; // 允许 flex 子元素收缩
}

.right-section {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.submit-btn {
  background: #ffb400;
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.more-icon {
  width: 24px;
  height: 24px;
  cursor: pointer;
}

// LanguageSelector 在白色背景下的样式调整
:deep(.language-selector) {
  .language-trigger {
    border: 1px solid rgba(0, 0, 0, 0.1);

    .language-text {
      color: #333;
      text-shadow: none;
    }

    .arrow-icon {
      filter: none;
    }
  }
}
</style>
